import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FaPlus, FaEdit } from 'react-icons/fa';
import Wrapper from '../../components/Wrapper';
import CollapsibleSection from '../../components/CollapsibleSection';
import { deviceApi } from '../../services/deviceApi';

const STAGE_BADGE = {
  GA: 'success',
  BETA: 'warning',
  ALPHA: 'secondary',
  DEPRECATED: 'danger',
};

const STAGES = ['ALPHA', 'BETA', 'GA', 'DEPRECATED'];

const compareSoftware = (newer, older) => {
  if (!older) return [];
  const changes = [];
  if (newer.version_tag !== older.version_tag) {
    changes.push({ field: 'Version', from: older.version_tag, to: newer.version_tag });
  }
  if (newer.stage !== older.stage) {
    changes.push({ field: 'Stage', from: older.stage, to: newer.stage });
  }
  if ((newer.release_notes || '') !== (older.release_notes || '')) {
    changes.push({ field: 'Release Notes', from: older.release_notes || '—', to: newer.release_notes || '—' });
  }
  const oldCompat = (older.compatible_hardware || []).join(', ') || '—';
  const newCompat = (newer.compatible_hardware || []).join(', ') || '—';
  if (oldCompat !== newCompat) {
    changes.push({ field: 'Compatible Hardware', from: oldCompat, to: newCompat });
  }
  return changes;
};

const emptyForm = () => ({
  software_type: 'DISPLAY',
  version_tag: '',
  stage: 'ALPHA',
  release_notes: '',
  compatible_hardware: [],
});

const SoftwareVersionGroup = ({ title, versions, onEdit }) => (
  <div className="mb-4">
    <h5 className="fw-semibold mb-3">{title}</h5>
    {versions.length === 0 ? (
      <Alert variant="info">No {title.toLowerCase()} versions found.</Alert>
    ) : (
      versions.map((version, index) => {
        const older = versions[index + 1];
        const changes = compareSoftware(version, older);
        return (
          <CollapsibleSection
            key={version.id}
            title={(
              <>
                <span>{version.version_tag}</span>
                <Badge bg={STAGE_BADGE[version.stage] || 'secondary'} className="ms-2">{version.stage}</Badge>
                {index === 0 && <Badge bg="success" className="ms-2">Latest</Badge>}
              </>
            )}
          >
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                {version.release_notes && (
                  <p className="mb-2"><strong>Release notes:</strong> {version.release_notes}</p>
                )}
                <p className="text-muted mb-0">
                  <strong>Compatible hardware:</strong> {(version.compatible_hardware || []).join(', ') || '—'}
                </p>
              </div>
              <Button variant="outline-primary" size="sm" onClick={() => onEdit(version)}>
                <FaEdit className="me-2" />
                Edit
              </Button>
            </div>

            {!older ? (
              <p className="text-muted mb-0">Earliest version in this category.</p>
            ) : changes.length === 0 ? (
              <p className="text-muted mb-0">
                No metadata changes from <Badge bg="light" text="dark">{older.version_tag}</Badge>.
              </p>
            ) : (
              <>
                <p className="mb-3">
                  Changes from <Badge bg="light" text="dark">{older.version_tag}</Badge>
                  {' '}to <Badge bg="primary">{version.version_tag}</Badge>
                </p>
                <div className="d-flex flex-column gap-2">
                  {changes.map((change) => (
                    <div key={`${change.field}-${version.id}`} className="border rounded p-2">
                      <strong>{change.field}</strong>
                      <div className="small text-muted mt-1">Previous: {change.from}</div>
                      <div className="small mt-1">Current: {change.to}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CollapsibleSection>
        );
      })
    )}
  </div>
);

const DeviceSoftwareVersion = () => {
  const [softwareList, setSoftwareList] = useState([]);
  const [hardwareList, setHardwareList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Unified modal state for Add and Edit
  const [modalConfig, setModalConfig] = useState({ show: false, mode: 'ADD', editId: null });
  const [formData, setFormData] = useState(emptyForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadSoftware = useCallback(() => {
    setLoading(true);
    deviceApi
      .getAllSoftware()
      .then(setSoftwareList)
      .catch((err) => setError(err?.response?.data?.error || 'Failed to load software versions.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadSoftware();
  }, [loadSoftware]);

  useEffect(() => {
    if (!modalConfig.show) return;
    deviceApi.getHardwareDropdown('device').then(setHardwareList).catch(() => setHardwareList([]));
  }, [modalConfig.show]);

  const displayVersions = useMemo(
    () => softwareList
      .filter((s) => s.software_type === 'DISPLAY')
      .sort((a, b) => b.version_tag.localeCompare(a.version_tag, undefined, { numeric: true, sensitivity: 'base' })),
    [softwareList]
  );
  
  const deviceVersions = useMemo(
    () => softwareList
      .filter((s) => s.software_type === 'DEVICE')
      .sort((a, b) => b.version_tag.localeCompare(a.version_tag, undefined, { numeric: true, sensitivity: 'base' })),
    [softwareList]
  );

  const toggleCompatibleHardware = (versionTag) => {
    setFormData((prev) => {
      const selected = prev.compatible_hardware.includes(versionTag)
        ? prev.compatible_hardware.filter((tag) => tag !== versionTag)
        : [...prev.compatible_hardware, versionTag];
      return { ...prev, compatible_hardware: selected };
    });
  };

  const handleOpenAdd = () => {
    setFormData(emptyForm());
    setFormError('');
    setModalConfig({ show: true, mode: 'ADD', editId: null });
  };

  const handleOpenEdit = (version) => {
    setFormData({
      software_type: version.software_type,
      version_tag: version.version_tag,
      stage: version.stage || 'ALPHA',
      release_notes: version.release_notes || '',
      compatible_hardware: version.compatible_hardware || [],
    });
    setFormError('');
    setModalConfig({ show: true, mode: 'EDIT', editId: version.id });
  };

  const handleCloseModal = () => {
    setModalConfig({ ...modalConfig, show: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.version_tag.trim()) {
      setFormError('Version tag is required.');
      return;
    }
    if (formData.compatible_hardware.length === 0) {
      setFormError('Select at least one compatible hardware version.');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      const payload = {
        version_tag: formData.version_tag.trim(),
        software_type: formData.software_type,
        stage: formData.stage,
        release_notes: formData.release_notes.trim() || null,
        compatible_hardware: formData.compatible_hardware,
      };

      if (modalConfig.mode === 'EDIT') {
        await deviceApi.updateSoftware(modalConfig.editId, payload);
      } else {
        await deviceApi.createSoftware(payload);
      }

      handleCloseModal();
      loadSoftware();
    } catch (err) {
      setFormError(err?.response?.data?.error || `Failed to ${modalConfig.mode === 'EDIT' ? 'update' : 'create'} software version.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrapper>
      <div className="reports-container py-4 w-100">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
          <h1 className="fw-bolder mb-0">Software Version</h1>
          <Button variant="dark" onClick={handleOpenAdd}>
            <FaPlus className="me-2" />
            Add Version
          </Button>
        </div>

        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {!loading && !error && (
          <>
            <SoftwareVersionGroup 
              title="Display Software" 
              versions={displayVersions} 
              onEdit={handleOpenEdit} 
            />
            <SoftwareVersionGroup 
              title="Device / Firmware Software" 
              versions={deviceVersions} 
              onEdit={handleOpenEdit} 
            />
          </>
        )}
      </div>

      <Modal show={modalConfig.show} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalConfig.mode === 'EDIT' ? 'Edit' : 'Add'} Software Version</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label>Software Type <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={formData.software_type}
                onChange={(e) => setFormData((prev) => ({ ...prev, software_type: e.target.value }))}
                disabled={modalConfig.mode === 'EDIT'} // Usually unsafe to change type after creation, remove disabled if backend supports it
              >
                <option value="DISPLAY">Display Software</option>
                <option value="DEVICE">Device / Firmware Software</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Version Tag <span className="text-danger">*</span></Form.Label>
              <Form.Control
                value={formData.version_tag}
                onChange={(e) => setFormData((prev) => ({ ...prev, version_tag: e.target.value }))}
                placeholder="e.g. 1.2.0"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Stage</Form.Label>
              <Form.Select
                value={formData.stage}
                onChange={(e) => setFormData((prev) => ({ ...prev, stage: e.target.value }))}
              >
                {STAGES.map((stage) => (
                  <option key={stage} value={stage}>{stage}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Release Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.release_notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, release_notes: e.target.value }))}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Compatible Hardware <span className="text-danger">*</span></Form.Label>
              <div className="border rounded p-3 d-flex flex-column gap-2">
                {hardwareList.length === 0 ? (
                  <span className="text-muted">No hardware versions available.</span>
                ) : (
                  hardwareList.map((hw) => (
                    <Form.Check
                      key={hw.id}
                      type="checkbox"
                      id={`hw-${hw.id}`}
                      label={hw.version_tag}
                      checked={formData.compatible_hardware.includes(hw.version_tag)}
                      onChange={() => toggleCompatibleHardware(hw.version_tag)}
                    />
                  ))
                )}
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={saving}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : (modalConfig.mode === 'EDIT' ? 'Save Changes' : 'Add Version')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Wrapper>
  );
};

export default DeviceSoftwareVersion;